import { PublisherInterface } from '@chili-publish/publisher-interface'
import { useEffect, useState } from 'react'

import ChiliVariables from 'shared/components/Tools/services/ChiliVariables'

const variablesApi = ChiliVariables.getInstance()

export const checkIfPageCreatedAutomatically = async () => {
  for (const variable of VARIABLES_FOR_CHECK) {
    const variableValue = await variablesApi.getVariable(variable)
    if (variableValue) {
      return true
    }
  }

  return false
}

// Variable examples to check if the page was created automatically
// TODO: add some variable to the templates (e.g. "created_automatically") to easily check if the page was created automatically
const VARIABLES_FOR_CHECK = [
  // Collages
  'photo_vertical_1',
  'photo_horizontal_1',
  // Profile
  'first_name',
  // Quotes
  'quote_small_1_quote',
  'quote_medium_1_quote',
  'quote_large_1_quote',
  // Reports
  'title',
  // Rankings
  'ranking_1_question'
]

const EXCLUDED_VARIABLE_TAGS = [
  'page_number'
]

export const useEditorAutomationPageTransform = ({
  publisherConnector,
  isDocumentRendered,
  saveDocument
}: {
  publisherConnector: PublisherInterface | null
  isDocumentRendered: boolean
  saveDocument: () => Promise<void>
}) => {
  const [isTransforming, setIsTransforming] = useState(false)
  const [loadingPercent, setLoadingPercent] = useState(0)

  const checkIfAlreadyTransformed = async () => {
    try {
      const alreadyTransformedVariable =
        await variablesApi.getVariable('auto_trasformed')
      if (alreadyTransformedVariable) {
        return true
      }

      return false
    } catch (error) {
      return false
    }
  }

 
  const getFramesCount = async () => {
    const framesCount = Number.parseInt(
      (
        (await publisherConnector?.getObject(
          'document.pages[0].frames'
        )) as any
      ).count
    )

    return framesCount
  }

  const createAlreadyTransformedVariable = async () => {
    try {
      await variablesApi.createVariable('auto_trasformed', true)
    } catch (error) {
      console.error('Error creating already transformed variable', error)
    }
  }

  const switchFrameTextSourceForEditing = async (
    frameSelector: string
  ) => {
    await publisherConnector?.setProperty(
      frameSelector,
      'isVariable',
      false
    )
  }

  const transformPage = async () => {
    const framesCount = await getFramesCount()
    if (!framesCount) {
      console.error('No frames found', framesCount)
      return
    }

    for (let i = 0; i < framesCount; i++) {
      const frame = (await publisherConnector?.getObject(
        `document.pages[0].frames[${i}]`
      )) as any

      if (frame.type !== 'text') {
        continue
      }

      if (!Boolean(frame.isVariable)) {
        continue
      }

      if (EXCLUDED_VARIABLE_TAGS.includes(frame.tag)) {
        continue
      }

      const { outputTextFlow } = frame
      if (!outputTextFlow) {
        console.error('No output text flow found', frame)
        continue
      }

      await switchFrameTextSourceForEditing(
        frame.javaScriptDescriptor
      )

      await publisherConnector?.executeFunction(
        frame.javaScriptDescriptor,
        'ImportTextFlow',
        outputTextFlow
      )

      await createAlreadyTransformedVariable()

      setLoadingPercent(Math.round((i / framesCount) * 100))
    }

    await saveDocument()
  }

  useEffect(() => {
    const transformWrapper = async () => {
      if (await checkIfAlreadyTransformed()) {
        return
      }

      if (!(await checkIfPageCreatedAutomatically())) {
        return
      }

      setIsTransforming(true)
      await transformPage()
      setIsTransforming(false)
    }

    if (isDocumentRendered && publisherConnector) {
      transformWrapper()
    }
  }, [isDocumentRendered, publisherConnector])

  return {
    isTransforming,
    loadingPercent
  }
}
